import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "react-query";
import io from "socket.io-client";
import useCurrentUser from "../../useCurrentUser";
import useUrls from "../../useUrls";
import useDraft from "./useDraft";

// noinspection JSUndefinedPropertyAssignment,JSUndefinedPropertyAssignment,JSCheckFunctionSignatures
export default function useDraftUpdates() {
  const currentUser = useCurrentUser();
  const { draftData } = useDraft();
  const [roomId, setRoomId] = useState(null);
  const queryClient = useQueryClient();
  const { socketUrl } = useUrls();

  /** @type {{current: Socket}} socketRef */
  const socketRef = useRef();

  const draftId = draftData?.draft.id;
  const username = currentUser?.obj.username;

  useEffect(() => {
    if (draftId) setRoomId(draftId);
  }, [draftId, setRoomId]);

  useEffect(() => {
    if (roomId && username) {
      // noinspection JSCheckFunctionSignatures
      socketRef.current = io(socketUrl, {
        query: { roomId, username },
      });

      socketRef.current.on("connect", () => {
        console.log("(socket) on - connect", socketRef.current.id);
      });

      socketRef.current.on("draft:autoPicked", async (byUsername) => {
        console.log(
          `(socket) on - draft:autoPicked - ${byUsername} auto picked`
        );
        await queryClient.refetchQueries(`/drafts/${draftId}/messages`);
        await queryClient.refetchQueries(`/drafts/${draftId}/picks`);
      });

      socketRef.current.on("draft:choseSpot", async (byUsername) => {
        console.log(
          `(socket) on - draft:choseSpot - ${byUsername} chose draft spot`
        );
        await queryClient.refetchQueries(`/drafts/${draftId}/messages`);
        await queryClient.refetchQueries(`/drafts/${draftId}/picks`);
      });

      socketRef.current.on("draft:ended", async (byUsername) => {
        console.log(`(socket) on - draft:ended - ${byUsername} ended draft`);
        await queryClient.refetchQueries(`/drafts/${draftId}`);
        await queryClient.refetchQueries(`/drafts/${draftId}/messages`);
        await queryClient.refetchQueries(`/drafts/${draftId}/picks`);
      });

      socketRef.current.on("draft:paused", async (byUsername) => {
        console.log(`(socket) on - draft:paused - ${byUsername} paused draft`);
        await queryClient.refetchQueries(`/drafts/${draftId}`);
        await queryClient.refetchQueries(`/drafts/${draftId}/messages`);
        await queryClient.refetchQueries(`/drafts/${draftId}/picks`);
      });

      socketRef.current.on("draft:picked", async (byUsername) => {
        console.log(`(socket) on - draft:picked - ${byUsername} picked player`);
        await queryClient.refetchQueries(`/drafts/${draftId}/messages`);
        await queryClient.refetchQueries(`/drafts/${draftId}/picks`);
      });

      socketRef.current.on("draft:didReset", async (byUsername) => {
        console.log(
          `(socket) on - draft:didReset - draft reset by ${byUsername}`
        );
        await queryClient.refetchQueries(`/drafts/${draftId}`);
        await queryClient.refetchQueries(`/drafts/${draftId}/messages`);
        await queryClient.refetchQueries(`/drafts/${draftId}/picks`);
      });

      socketRef.current.on("draft:resumed", async (byUsername) => {
        console.log(
          `(socket) on - draft:resumed - ${byUsername} resumed draft`
        );
        await queryClient.refetchQueries(`/drafts/${draftId}`);
        await queryClient.refetchQueries(`/drafts/${draftId}/messages`);
        await queryClient.refetchQueries(`/drafts/${draftId}/picks`);
      });

      socketRef.current.on("draft:started", async (byUsername) => {
        console.log(
          `(socket) on - draft:started - draft started by ${byUsername}`
        );
        await queryClient.refetchQueries(`/drafts/${draftId}`);
        await queryClient.refetchQueries(`/drafts/${draftId}/messages`);
      });

      socketRef.current.on("draft:undidChooseSpot", async (byUsername) => {
        console.log(
          `(socket) on - draft:undidChooseSpot - choose spot undone by ${byUsername}`
        );
        await queryClient.refetchQueries(`/drafts/${draftId}/messages`);
        await queryClient.refetchQueries(`/drafts/${draftId}/picks`);
      });

      socketRef.current.on("draft:undidPick", async (byUsername) => {
        console.log(
          `(socket) on - draft:undidPick - draft pick undone by ${byUsername}`
        );
        await queryClient.refetchQueries(`/drafts/${draftId}/messages`);
        await queryClient.refetchQueries(`/drafts/${draftId}/picks`);
      });

      socketRef.current.on("disconnect", async () => {
        console.log("(socket) on - disconnect", socketRef.current.id);
        await queryClient.refetchQueries(`/drafts/${draftId}`);
        await queryClient.refetchQueries(`/drafts/${draftId}/messages`);
        await queryClient.refetchQueries(`/drafts/${draftId}/picks`);
      });
    }

    return () => {
      if (socketRef.current) socketRef.current.disconnect();
    };
  }, [draftId, roomId, username, queryClient, socketUrl]);

  const emit = (name) => {
    if (!socketRef.current) return;
    console.log(`(socket) emit - draft:${name} - username: ${username}`);
    socketRef.current.emit(`draft:${name}`, username);
  };

  const autoPick = () => emit("autoPick");
  const chooseSpot = () => emit("chooseSpot");
  const end = () => emit("end");
  const pause = () => emit("pause");
  const pick = () => emit("pick");
  const resetDraft = () => emit("reset");
  const resume = () => emit("resume");
  const startDraft = () => emit("start");
  const undoChooseSpot = () => emit("undoChooseSpot");
  const undoPick = () => emit("undoPick");

  return {
    autoPick,
    chooseSpot,
    end,
    pause,
    pick,
    resetDraft,
    resume,
    startDraft,
    undoChooseSpot,
    undoPick,
  };
}
